import { useCallback, useEffect, useRef, useState } from "react";

const EDGE_TOLERANCE = 2;

const useScrollArrows = (step = 0.6) => {
    const ref = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const update = useCallback(() => {
        const node = ref.current;
        if (!node) return;
        const { scrollLeft, scrollWidth, clientWidth } = node;
        setCanScrollLeft(scrollLeft > EDGE_TOLERANCE);
        setCanScrollRight(
            scrollLeft + clientWidth < scrollWidth - EDGE_TOLERANCE,
        );
    }, []);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;

        const handleWheel = (event) => {
            if (node.scrollWidth <= node.clientWidth) return;
            const delta =
                Math.abs(event.deltaX) > Math.abs(event.deltaY)
                    ? event.deltaX
                    : event.deltaY;
            if (!delta) return;
            const atStart = node.scrollLeft <= EDGE_TOLERANCE && delta < 0;
            const atEnd =
                node.scrollLeft + node.clientWidth >=
                    node.scrollWidth - EDGE_TOLERANCE && delta > 0;
            if (atStart || atEnd) return;
            event.preventDefault();
            node.scrollLeft += delta;
        };

        update();
        node.addEventListener("scroll", update, { passive: true });
        node.addEventListener("wheel", handleWheel, { passive: false });

        const observer =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(update)
                : null;
        observer?.observe(node);
        Array.from(node.children).forEach((child) => observer?.observe(child));
        window.addEventListener("resize", update);

        return () => {
            node.removeEventListener("scroll", update);
            node.removeEventListener("wheel", handleWheel);
            observer?.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [update]);

    const scrollBy = useCallback(
        (direction) => {
            const node = ref.current;
            if (!node) return;
            node.scrollBy({
                left: direction * node.clientWidth * step,
                behavior: "smooth",
            });
        },
        [step],
    );

    return {
        ref,
        canScrollLeft,
        canScrollRight,
        hasOverflow: canScrollLeft || canScrollRight,
        scrollBy,
        update,
    };
};

export default useScrollArrows;
